import React, { useEffect, useState } from 'react';
import { Crown, Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  useGenerationFields,
  GENERATION_LANGUAGES,
  defaultGenerationLanguage,
} from 'modules/generationFields';
import { generateBankBatch } from 'modules/learnArtifacts/api';
import {
  useMaxQuestionsPerGeneration,
  useOpenUpgradeForLimit,
  useOpenUpgradeForFeature,
  useFeature,
  MAX_QUESTIONS_PER_GENERATION,
  FEATURES,
} from 'modules/entitlements';
import { UpgradeFieldBadge } from 'components/entitlements';
import {
  QUESTION_TYPE_ORDER,
  QUESTION_TYPE_META,
  isAdvancedQuestionType,
} from 'pages/QuestionBank/questionTypeMeta';
import { FieldPromptInput } from './FieldPromptInput';

// Smallest question count the bank flow accepts.
const MIN_QUESTIONS = 3;

// Unchanged default: an author who touches nothing gets exactly what this flow
// produced before question types became selectable.
const DEFAULT_QUESTION_TYPES = ['MULTIPLE_CHOICE'];

interface Props {
  documentId: string;
  open: boolean;
  onClose: () => void;
}

export const GenerateToBankModal: React.FC<Props> = ({ documentId, open, onClose }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  const [questionCount, setQuestionCount] = useState(8);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [language, setLanguage] = useState(() => defaultGenerationLanguage(i18n.language));
  const [fieldKey, setFieldKey] = useState('');
  const [fieldPrompt, setFieldPrompt] = useState('');
  const [questionTypes, setQuestionTypes] = useState<string[]>(DEFAULT_QUESTION_TYPES);
  const [isGenerating, setIsGenerating] = useState(false);

  // Per-plan cap on questions per generation (backend enforces; this is UX).
  const maxQuestions = useMaxQuestionsPerGeneration();
  const openUpgrade = useOpenUpgradeForLimit();
  const isRestricted = maxQuestions < MAX_QUESTIONS_PER_GENERATION;
  const overCap = questionCount > maxQuestions;

  // Advanced (non-basic) question types are a Growth+ capability. The backend
  // also filters them out per plan, so this is UX, not the gate.
  const canAdvancedTypes = useFeature(FEATURES.ADVANCED_QUESTION_TYPES);
  const openUpgradeForFeature = useOpenUpgradeForFeature();
  const isLockedType = (type: string) => !canAdvancedTypes && isAdvancedQuestionType(type);

  const toggleQuestionType = (type: string) => {
    // Picking a locked advanced type opens the upgrade dialog and leaves the
    // current selection alone, matching GenerateWithAIModal.
    if (isLockedType(type)) {
      openUpgradeForFeature(FEATURES.ADVANCED_QUESTION_TYPES);
      return;
    }
    setQuestionTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  // Start the default within the plan cap (e.g. 8 → 5 on Trial). The stepper
  // itself is NOT capped to the plan — the user may raise it past the cap and is
  // upsold on generate — so this only clamps the initial value when the cap
  // resolves, not the user's own input.
  useEffect(() => {
    setQuestionCount((c) => Math.min(c, maxQuestions));
  }, [maxQuestions]);

  const { fields } = useGenerationFields();

  const handleGenerate = async () => {
    if (questionTypes.length === 0) return;
    // Over the plan cap → open the upgrade dialog instead of a doomed request
    // (avoids the error toast + the 403-triggered dialog firing together).
    if (questionCount > maxQuestions) {
      openUpgrade(FEATURES.AI_QUESTIONS_PER_GENERATION, maxQuestions, questionCount);
      return;
    }
    setIsGenerating(true);
    try {
      await generateBankBatch({
        documentId,
        questionCount,
        questionTypes,
        difficulty,
        language,
        fieldKey: fieldKey || undefined,
        fieldPrompt: fieldPrompt.trim() || undefined,
      });
      // Generation runs as a background job and produces a DRAFT: the questions
      // are held on the artifact for review and reach the bank only when the
      // author adds them, choosing the subject at that point. The workspace
      // polls the artifact and offers the review dialog when it lands, so the
      // user can close this or navigate away.
      toast.info(t('generation_started'), {
        description: t('generating_questions_from_document'),
      });
      queryClient.invalidateQueries({ queryKey: ['learn-artifacts'] });
      onClose();
    } catch (err: unknown) {
      toast.error(t('generation_failed'), {
        description: (err as Error).message || t('please_try_again'),
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      {/* No description to point at — tell Radix explicitly, or it warns. */}
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t('generate_questions_to_bank')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* No subject here: the destination is chosen in the review dialog,
              after the questions exist and can actually be judged. */}
          <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            {t('choose_subject_when_saving')}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className="text-sm font-medium">{t('number_of_questions')}</label>
                {isRestricted && (
                  <UpgradeFieldBadge
                    featureKey={FEATURES.AI_QUESTIONS_PER_GENERATION}
                    limit={maxQuestions}
                    requested={questionCount}
                    tooltip={t('max_questions_plan_hint', { count: maxQuestions })}
                  />
                )}
              </div>
              <Input
                type="number"
                min={MIN_QUESTIONS}
                max={MAX_QUESTIONS_PER_GENERATION}
                value={questionCount}
                onChange={(e) =>
                  setQuestionCount(
                    Math.min(MAX_QUESTIONS_PER_GENERATION, Math.max(MIN_QUESTIONS, Number(e.target.value))),
                  )
                }
                className={cn(overCap && 'border-amber-400 focus-visible:ring-amber-400/50')}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{t('difficulty')}</label>
              <Select
                value={difficulty}
                onValueChange={(v) => setDifficulty(v as 'easy' | 'medium' | 'hard')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">{t('easy')}</SelectItem>
                  <SelectItem value="medium">{t('medium')}</SelectItem>
                  <SelectItem value="hard">{t('hard')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">{t('language')}</label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GENERATION_LANGUAGES.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">{t('question_types')}</label>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {QUESTION_TYPE_ORDER.map((type) => {
                const locked = isLockedType(type);
                const checked = questionTypes.includes(type);
                return (
                  <label
                    key={type}
                    className={cn(
                      'flex items-center gap-2 text-sm',
                      locked ? 'cursor-pointer text-muted-foreground' : 'cursor-pointer',
                    )}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleQuestionType(type)}
                      aria-label={t(QUESTION_TYPE_META[type].labelKey)}
                    />
                    <span className="truncate">{t(QUESTION_TYPE_META[type].labelKey)}</span>
                    {locked && <Crown className="size-3 shrink-0 text-amber-500" aria-hidden />}
                  </label>
                );
              })}
            </div>
            {questionTypes.length === 0 && (
              <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-500">
                {t('select_at_least_one_question_type')}
              </p>
            )}
          </div>

          <FieldPromptInput
            fields={fields}
            fieldKey={fieldKey}
            fieldPrompt={fieldPrompt}
            onFieldKeyChange={setFieldKey}
            onFieldPromptChange={setFieldPrompt}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isGenerating}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={questionTypes.length === 0 || isGenerating}
            className="gap-1.5"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('generating')}...
              </>
            ) : (
              t('generate')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
